import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOJsonLD from '@/components/base/SEOJsonLD';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <Breadcrumb />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
            <i className="ri-article-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="text-xl text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "author": { "@type": "Person", "name": article.author },
    "datePublished": article.publishDate,
    "image": article.cover,
    "publisher": {
      "@type": "Organization",
      "name": "红桃tv",
      "url": "https://hongtaotv.com/"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://hongtaotv.com/" },
      { "@type": "ListItem", "position": 2, "name": article.category },
      { "@type": "ListItem", "position": 3, "name": article.title }
    ]
  };

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <SEOJsonLD data={articleSchema} />
      <SEOJsonLD data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-primary-500">{article.category}</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800">正文</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-relaxed">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views} 阅读
              </span>
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="aspect-[2/1] rounded-lg overflow-hidden mb-8">
              <img
                src={article.cover}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
              {article.content.split('\n').map((paragraph, idx) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return <br key={idx} />;
                if (trimmed.startsWith('## ')) {
                  return (
                    <h2 key={idx} className="font-heading text-xl md:text-2xl text-foreground-900 mt-8 mb-4">
                      {trimmed.replace('## ', '')}
                    </h2>
                  );
                }
                if (/^\d+\.\s/.test(trimmed)) {
                  return (
                    <p key={idx} className="mb-3 pl-4 border-l-2 border-primary-300 text-foreground-700">
                      <strong>{trimmed}</strong>
                    </p>
                  );
                }
                return (
                  <p key={idx} className="mb-4 text-foreground-700">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            <div className="border-t border-background-200/70 mt-10 pt-6 flex items-center justify-between">
              <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 transition-colors">
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-secondary-100 flex items-center justify-center cursor-pointer transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-600 text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-secondary-100 flex items-center justify-center cursor-pointer transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-600 text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-secondary-100 flex items-center justify-center cursor-pointer transition-colors" aria-label="复制链接">
                  <i className="ri-link text-foreground-600 text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/70 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.cover}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <span className="text-xs text-primary-500 bg-primary-50 px-2 py-0.5 rounded mb-2 inline-block">
                        {ra.category}
                      </span>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}